import { CarrierLogo } from "@/components/common/CarrierLogo";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { cn } from "@/lib/utils";

export interface LegendItem {
  label: string;
  color: string;
  /** When set, show a theme-aware carrier logo instead of the text label. */
  carrierCode?: string;
}

interface ChartLegendProps {
  items: LegendItem[];
  /** Blur the series labels (restricted PII). */
  mask?: boolean;
  /** Series under the pointer — others dim. */
  hoveredIndex?: number | null;
  /** Isolated series — click again to show all. */
  selectedIndex?: number | null;
  onHover?: (index: number | null) => void;
  onSelect?: (index: number) => void;
}

/** Compact swatch + label key rendered below a chart. */
export function ChartLegend({
  items,
  mask,
  hoveredIndex = null,
  selectedIndex = null,
  onHover,
  onSelect,
}: ChartLegendProps) {
  if (items.length === 0) return null;

  const { preferences } = useUserPreferences();
  const interactive = onHover != null || onSelect != null;
  const emphasis = hoveredIndex ?? selectedIndex;

  const renderLabel = (item: LegendItem) => {
    if (item.carrierCode) {
      return (
        <CarrierLogo
          carrier={item.carrierCode}
          size={14}
          showLogo={preferences.showCarrierLogos}
          logoOnly={preferences.showCarrierLogos}
          className={cn(
            preferences.showCarrierLogos && "max-h-[14px]",
            mask && "sensitive-blur",
          )}
        />
      );
    }
    return (
      <span className={cn("truncate", mask && "sensitive-blur")}>
        {item.label}
      </span>
    );
  };

  return (
    <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
      {items.map((item, i) => {
        const selected = selectedIndex === i;
        const hovered = hoveredIndex === i;
        const dimmed =
          selectedIndex != null
            ? !selected && !hovered
            : emphasis != null && !hovered;
        const className = cn(
          "flex items-center gap-1.5 border-0 bg-transparent p-0 text-left text-[11px] text-muted-foreground transition-none",
          interactive && "cursor-pointer rounded-sm hover:text-foreground",
          selected && "font-medium text-foreground",
          dimmed && "opacity-35",
        );
        const swatch = (
          <span
            className="inline-block size-2.5 shrink-0 rounded-[3px]"
            style={{ backgroundColor: item.color }}
          />
        );
        if (!interactive) {
          return (
            <li key={`${item.label}-${i}`} className={className}>
              {swatch}
              {renderLabel(item)}
            </li>
          );
        }

        return (
          <li key={`${item.label}-${i}`}>
            <button
              type="button"
              aria-pressed={selected}
              className={className}
              onMouseEnter={() => onHover?.(i)}
              onMouseLeave={() => onHover?.(null)}
              onFocus={() => onHover?.(i)}
              onBlur={() => onHover?.(null)}
              onClick={() => onSelect?.(i)}
            >
              {swatch}
              {renderLabel(item)}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
